import React, { useContext } from "react";
import { ThemeContext } from "@fluentui/react-theme-provider";
import { ContractOnboardingStrings } from "@/screen/Map/contractOnboarding.strings";
import * as S from "@/screen/Map/Components/ContractMapStepCardStyles";

export interface ContractMapStepCardTask
{
    title: string;
    description?: string;
    actionLabel: string;
    onActionClick: () => void;
    isActionDisabled?: boolean;
}

export interface ContractMapStepCardProps
{
    title: string;
    stepKey: string;
    description: React.ReactNode;
    extraDescription?: string;
    actionLabel?: string;
    onActionClick?: () => void;
    isActionDisabled?: boolean;
    isCurrent?: boolean;
    isDimmed?: boolean;
    noticeText?: string;
    onGuideClick?: () => void;
    guideButtonId?: string;
    tasks?: ContractMapStepCardTask[];
}

export const ContractMapStepCard: React.FC<ContractMapStepCardProps> = (props) =>
{
    const
    {
        title,
        stepKey,
        description,
        extraDescription,
        actionLabel,
        onActionClick,
        isActionDisabled = false,
        isCurrent = false,
        isDimmed = false,
        noticeText,
        onGuideClick,
        guideButtonId,
        tasks,
    } = props;

    const theme = useContext(ThemeContext);
    const showGuideButton = typeof onGuideClick === "function";
    const showActionButton = typeof actionLabel === "string" && actionLabel.length > 0;
    const hasTasks = Array.isArray(tasks) && tasks.length > 0;
    // Con tareas internas no pintamos footer de acción; la guía sí puede convivir si se pide.
    const showFooter = showGuideButton || (showActionButton && !hasTasks);

    return(
        <S.CardRoot theme={theme} $isDimmed={isDimmed} $isCurrent={isCurrent}>
            <S.CardHeader>
                <S.CardTitle>{title}</S.CardTitle>
                <S.CardBadge theme={theme}>{stepKey}</S.CardBadge>
            </S.CardHeader>
            <S.CardBody>
                {description &&
                (
                    <S.CardDescription>{description}</S.CardDescription>
                )}
                {extraDescription &&
                (
                    <S.CardDescription>{extraDescription}</S.CardDescription>
                )}
                {noticeText &&
                (
                    <S.CardNotice>{noticeText}</S.CardNotice>
                )}
                {hasTasks &&
                (
                    <S.TaskList>
                        {tasks!.map((task) =>
                        (
                            <S.TaskRow key={`${task.title}-${task.actionLabel}`}>
                                <S.TaskContent>
                                    <S.TaskTitle>{task.title}</S.TaskTitle>
                                    {task.description &&
                                    (
                                        <S.TaskDescription>{task.description}</S.TaskDescription>
                                    )}
                                </S.TaskContent>
                                <S.TaskActionButton type="button" $isDisabled={Boolean(task.isActionDisabled)}
                                    disabled={Boolean(task.isActionDisabled)}
                                    onClick={task.isActionDisabled ? undefined : task.onActionClick}
                                >
                                    {task.actionLabel}
                                </S.TaskActionButton>
                            </S.TaskRow>
                        ))}
                    </S.TaskList>
                )}
            </S.CardBody>
            {showFooter &&
            (
                <S.CardFooter>
                    {showGuideButton &&
                    (
                        <S.GuideButton type="button" id={guideButtonId} onClick={onGuideClick}>
                            {ContractOnboardingStrings.viewGuide}
                        </S.GuideButton>
                    )}
                    {showActionButton && !hasTasks &&
                    (
                        <S.ActionButton
                            type="button"
                            $isDisabled={isActionDisabled}
                            disabled={isActionDisabled}
                            onClick={isActionDisabled ? undefined : onActionClick}
                        >
                            {actionLabel}
                        </S.ActionButton>
                    )}
                </S.CardFooter>
            )}
        </S.CardRoot>
    );
};
